import { createContext, useContext, useEffect, useRef, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { GROWTH_HUB_FUNCTIONS, LOCAL_FUNCTIONS, TRACKING_HEADERS } from '@/integrations/supabase/growthHubClient';
import { useScrollDepthTracking } from '@/hooks/useScrollDepthTracking';
import { useCoreWebVitals } from '@/hooks/useCoreWebVitals';
import { useIdentityResolution } from '@/hooks/useIdentityResolution';
import { captureMarketingParams } from '@/lib/firstPartyCookies';
import { captureAttribution } from '@/lib/marketingAttribution';
import {
  initializeConsentMode,
  initializeGoogleTracking,
  applyRemoteConfig,
  applyBrandAnalytics,
  isBrandTrackingEnabled,
  trackGooglePageView,
  installLegacyGlobals,
  getGoogleTrackingConfig,
} from '@/lib/googleTracking';
import { installTrackingDebug } from '@/lib/trackingDebug';
import { installAutofillCapture } from '@/lib/autofillCapture';
import { installPhoneClickNotifier } from '@/lib/phoneClickNotifier';
import { installContactLinkTracker } from '@/lib/contactLinkTracker';
import { installVisitorIntel, viPageView } from '@/lib/visitorIntel';
import { detectBrandProfile } from '@/lib/brandRegistry';
import { getBrandAnalytics } from '@/lib/brandAnalytics';
import { fireTrackingHealthPing } from '@/lib/trackingHealthPing';

// Per-brand IDs — set at runtime from brandAnalytics, overridden by remote config blanks.
let META_PIXEL_ID = '';
let CLARITY_PROJECT_ID = '';
let META_ENABLED = true;
let CLARITY_ENABLED = true;

const TRACKING_CONFIG_URL = 'https://xvcoghpmuaetdiyzoliy.supabase.co/functions/v1/get-tracking-config';

// Fetch IDs from Growth Hub source-of-truth; falls back to env defaults on failure.
// Remote values only fill blanks — they never overwrite a non-empty env value.
const fetchTrackingConfig = async (): Promise<void> => {
  try {
    const res = await fetch(TRACKING_CONFIG_URL, { method: 'GET' });
    if (!res.ok) return;
    const cfg = await res.json();
    if (cfg?.meta_pixel_id) META_PIXEL_ID = cfg.meta_pixel_id;
    if (cfg?.clarity_project_id) CLARITY_PROJECT_ID = cfg.clarity_project_id;
    applyRemoteConfig({
      ga4_measurement_id: cfg?.ga4_measurement_id ?? null,
      google_ads_id: cfg?.google_ads_id ?? null,
      google_ads_conversion_label: cfg?.google_ads_conversion_label ?? null,
      gtm_id: cfg?.gtm_id ?? null,
    });
    // Re-init Google now that remote IDs have filled any blanks.
    initializeGoogleTracking();
  } catch {
    // ignore — env defaults remain
  }
};


// ---- Tracking hardening ------------------------------------------------------
// The local visitor-tracking proxy accepts browser tracking and forwards it
// upstream best-effort. Some older scripts can still emit `heartbeat` payloads,
// so this shim converts them into `track_event` to keep tracking resilient.
//
// Additionally, the external Growth Hub `visualizer-tracking` function can
// intermittently return 4xx/5xx. Some callers (including SDK wrappers) throw on
// non-2xx responses, which can cause a blank-screen in preview/dev. We normalize
// those failures to a 200 response so the app never crashes due to tracking.
const installGrowthHubHeartbeatShim = () => {
  const w = window as any;
  if (w.__obcHeartbeatShimInstalled) return;
  w.__obcHeartbeatShimInstalled = true;

  const originalFetch: typeof window.fetch = window.fetch.bind(window);

  window.fetch = (async (input: RequestInfo | URL, init?: RequestInit) => {
    try {
      const url = typeof input === 'string'
        ? input
        : input instanceof URL
          ? input.toString()
          : (input as Request).url;

      const isVisitorTracking = !!url && url.includes('/functions/v1/visitor-tracking');
      const isVisualizerTracking = !!url && url.includes('/functions/v1/visualizer-tracking');

      // Only intercept tracking endpoints
      if (!isVisitorTracking && !isVisualizerTracking) {
        return originalFetch(input as any, init);
      }

      // visualizer-tracking: convert failures to 200 to avoid callers throwing
      if (isVisualizerTracking) {
        try {
          const res = await originalFetch(input as any, init);
          if (res.ok) return res;
          // Consume body (avoid resource leaks), then normalize
          await res.clone().text().catch(() => {});
          return new Response(JSON.stringify({ success: false }), {
            status: 200,
            headers: { 'Content-Type': 'application/json' },
          });
        } catch {
          return new Response(JSON.stringify({ success: false }), {
            status: 200,
            headers: { 'Content-Type': 'application/json' },
          });
        }
      }

      // From here on, it's visitor-tracking only
      // Wrap everything in try/catch so a 5xx/BOOT_ERROR never crashes the app
      try {
        // Extract body depending on Request/RequestInit shape
        const method = (init?.method || (input instanceof Request ? input.method : 'GET')).toUpperCase();
        if (method === 'GET' || method === 'HEAD') {
          const res = await originalFetch(input as any, init);
          if (res.ok) return res;
          return new Response(JSON.stringify({ success: false }), {
            status: 200,
            headers: { 'Content-Type': 'application/json' },
          });
        }

        const bodyFromInit = init?.body;
        const bodyText = typeof bodyFromInit === 'string'
          ? bodyFromInit
          : (input instanceof Request ? await input.clone().text() : null);

        if (!bodyText) {
          const res = await originalFetch(input as any, init);
          if (res.ok) return res;
          return new Response(JSON.stringify({ success: false }), {
            status: 200,
            headers: { 'Content-Type': 'application/json' },
          });
        }

        const parsed = JSON.parse(bodyText);

        // Convert unsupported heartbeat → supported track_event
        let finalInit: RequestInit;
        if (parsed?.action === 'heartbeat') {
          const data = parsed?.data || {};
          const converted = {
            action: 'track_event',
            data: {
              ...data,
              event_type: data.event_type || 'heartbeat',
              event_name: data.event_name || 'heartbeat',
              page_url: data.page_url || window.location.href,
              page_path: data.page_path || window.location.pathname,
              page_title: data.page_title || document.title,
            },
          };
          finalInit = {
            ...(init || {}),
            method,
            body: JSON.stringify(converted),
            headers: {
              ...(init?.headers || (input instanceof Request ? Object.fromEntries(input.headers.entries()) : {})),
              'Content-Type': 'application/json',
            } as any,
          };
        } else {
          finalInit = init || {};
        }

        const res = await originalFetch(typeof input === 'string' ? input : (input instanceof URL ? input.toString() : (input as Request).url), finalInit);
        if (res.ok) return res;
        // Normalize non-2xx to 200 so callers never throw
        await res.clone().text().catch(() => {});
        return new Response(JSON.stringify({ success: false }), {
          status: 200,
          headers: { 'Content-Type': 'application/json' },
        });
      } catch {
        // Network/parse failure — return synthetic 200
        return new Response(JSON.stringify({ success: false }), {
          status: 200,
          headers: { 'Content-Type': 'application/json' },
        });
      }
    } catch {
      // Never let tracking-related interception break app behavior
      return new Response(JSON.stringify({ success: false }), {
        status: 200,
        headers: { 'Content-Type': 'application/json' },
      });
    }
  }) as any;
};

interface TrackingContextType {
  visitorId: string;
  sessionId: string;
}

const TrackingContext = createContext<TrackingContextType | null>(null);

export const useTrackingContext = () => {
  const context = useContext(TrackingContext);
  if (!context) {
    throw new Error('useTrackingContext must be used within TrackingProvider');
  }
  return context;
};

// Generate unique IDs
const generateId = () => `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

const hasLeadContactData = (eventData: Record<string, unknown> = {}) => {
  const email = typeof eventData.email === 'string' ? eventData.email.trim() : '';
  const phone = typeof eventData.phone === 'string' ? eventData.phone.replace(/\D/g, '') : '';
  const address = typeof eventData.address === 'string' ? eventData.address.trim() : '';
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) || phone.length >= 7 || address.length >= 5;
};

// Get or create visitor ID (persists across sessions)
const getVisitorId = (): string => {
  let visitorId = localStorage.getItem('obc_visitor_id');
  if (!visitorId) {
    visitorId = generateId();
    localStorage.setItem('obc_visitor_id', visitorId);
  }
  return visitorId;
};

// Get or create session ID (new per session)
const getSessionId = (): string => {
  let sessionId = sessionStorage.getItem('obc_session_id');
  if (!sessionId) {
    sessionId = generateId();
    sessionStorage.setItem('obc_session_id', sessionId);
  }
  return sessionId;
};

// Capture UTM parameters from URL
const captureUtmParams = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const utmParams: Record<string, string> = {};
  
  ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'gclid', 'fbclid'].forEach(param => {
    const value = urlParams.get(param);
    if (value) {
      utmParams[param] = value;
    }
  });
  
  if (Object.keys(utmParams).length > 0) {
    sessionStorage.setItem('obc_utm_params', JSON.stringify(utmParams));
  }
  
  return utmParams;
};

// Get stored UTM params
export const getStoredUtmParams = (): Record<string, string> => {
  try {
    const stored = sessionStorage.getItem('obc_utm_params');
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
};

interface TrackingProviderProps {
  children: ReactNode;
}

// Inner component that uses scroll tracking hook
const TrackingProviderInner = ({ children }: TrackingProviderProps) => {
  useScrollDepthTracking();
  useCoreWebVitals();
  useIdentityResolution();
  
  return <>{children}</>;

};

// Helper to defer non-critical work until browser is idle
const deferUntilIdle = (callback: () => void, timeout = 3000) => {
  if ('requestIdleCallback' in window) {
    (window as any).requestIdleCallback(callback, { timeout });
  } else {
    setTimeout(callback, 2000);
  }
};

export const TrackingProvider = ({ children }: TrackingProviderProps) => {
  const location = useLocation();
  const visitorIdRef = useRef<string>('');
  const sessionIdRef = useRef<string>('');
  const scriptsLoadedRef = useRef(false);

  // Initialize IDs and capture marketing parameters
  useEffect(() => {
    visitorIdRef.current = getVisitorId();
    sessionIdRef.current = getSessionId();
    captureUtmParams();

    // Capture first-party marketing cookies (fbclid, gclid) + full attribution
    // (gclid/gbraid/wbraid/UTMs/referrer/landing) for first-touch + last-touch.
    captureMarketingParams();
    captureAttribution();
    installPhoneClickNotifier();
    installContactLinkTracker();
    installVisitorIntel();


    // Fire start_session exactly once per session so visitor_sessions gets a row
    if (!sessionStorage.getItem('obc_session_started')) {
      sessionStorage.setItem('obc_session_started', '1');
      fetch(LOCAL_FUNCTIONS.visitorTracking, {
        method: 'POST',
        headers: TRACKING_HEADERS,
        body: JSON.stringify({
          action: 'start_session',
          data: {
            session_id: sessionIdRef.current,
            visitor_id: visitorIdRef.current,
            landing_page: window.location.pathname,
            referrer: document.referrer || undefined,
            user_agent: navigator.userAgent,
            screen_width: window.screen.width,
            screen_height: window.screen.height,
            viewport_width: window.innerWidth,
            viewport_height: window.innerHeight,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            ...getStoredUtmParams(),
          },
        }),
      }).catch(() => {});
    }
  }, []);

  // Visitor Intelligence page views (first-party)
  useEffect(() => { viPageView(); }, [location.pathname]);

  // Load tracking scripts - DEFERRED to not block LCP
  useEffect(() => {
    if (scriptsLoadedRef.current) return;
    scriptsLoadedRef.current = true;

    // 0. Resolve per-brand analytics config from the current hostname.
    //    Brand wins over env; remote config only fills blanks afterward.
    const brand = detectBrandProfile();
    const brandCfg = getBrandAnalytics(brand.brand);
    applyBrandAnalytics(brandCfg);
    META_PIXEL_ID = brandCfg.tagIds.metaPixelId;
    CLARITY_PROJECT_ID = brandCfg.tagIds.clarityProjectId;
    META_ENABLED = brandCfg.enabled && brandCfg.platforms.meta && !!META_PIXEL_ID;
    CLARITY_ENABLED = brandCfg.enabled && brandCfg.platforms.clarity && !!CLARITY_PROJECT_ID;

    // Brand kill-switch: skip all tag injection entirely.
    if (!isBrandTrackingEnabled()) return;

    // 1. Initialize Consent Mode v2 + Google tags synchronously (lightweight; no network).
    // Consent default is granted (no-banner, track-all policy) — matches index.html.
    initializeConsentMode();
    initializeGoogleTracking();
    installLegacyGlobals();
    installTrackingDebug();
    installAutofillCapture();

    // 2. Fetch remote IDs from Growth Hub; remote values only fill blanks.
    //    Re-runs initializeGoogleTracking() once values arrive.
    fetchTrackingConfig();

    // 3. Fire the initial GA4 page_view exactly once (gtag is configured with
    //    send_page_view:false so the auto pageview is suppressed).
    trackGooglePageView({
      page_path: window.location.pathname,
      page_title: document.title,
    });

    // 4. Defer non-critical local trackers + Clarity until idle.
    deferUntilIdle(() => {
      installGrowthHubHeartbeatShim();
      // Fire once-per-session diagnostic so admin can compare hosting traffic
      // against pixel-fired traffic. Anon-safe insert into analytics_events.
      void fireTrackingHealthPing();

      // Local OBC Tracker (avoiding external script that sends unsupported heartbeat actions)
      (window as any).obcTracker = {
        trackEvent: (eventName: string, eventData: Record<string, unknown> = {}) => {
          if (
            ['form_submission', 'form_submit', 'lead', 'generate_lead'].includes(eventName) &&
            !hasLeadContactData(eventData)
          ) {
            console.debug('Skipping OBC lead event: no usable contact data', { eventName });
            return;
          }

          fetch(LOCAL_FUNCTIONS.visitorTracking, {
            method: 'POST',
            headers: TRACKING_HEADERS,
            body: JSON.stringify({
              action: 'track_event',
              data: {
                session_id: sessionStorage.getItem('obc_session_id') || '',
                visitor_id: localStorage.getItem('obc_visitor_id') || '',
                event_type: eventName,
                event_name: eventName,
                page_url: window.location.href,
                page_path: window.location.pathname,
                page_title: document.title,
                ...eventData,
              },
            }),
          }).catch(() => {
            // Silently fail - tracking should not break the app
          });
        },
      };

      // Microsoft Clarity (gated by brand platforms.clarity)
      if (CLARITY_ENABLED) {
        const clarityScript = document.createElement('script');
        clarityScript.innerHTML = `
          (function(c,l,a,r,i,t,y){
            c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
            t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
            y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
          })(window, document, "clarity", "script", "${CLARITY_PROJECT_ID}");
        `;
        document.head.appendChild(clarityScript);
      }
    });


    // 3. Meta Pixel - EXTRA DEFERRED (heaviest third-party script); gated per-brand
    if (!META_ENABLED) return;
    const injectMetaPixel = () => {
      const pixelScript = document.createElement('script');
      pixelScript.innerHTML = `
        !function(f,b,e,v,n,t,s)
        {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
        n.callMethod.apply(n,arguments):n.queue.push(arguments)};
        if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
        n.queue=[];t=b.createElement(e);t.async=!0;
        t.src=v;s=b.getElementsByTagName(e)[0];
        s.parentNode.insertBefore(t,s)}(window, document,'script',
        'https://connect.facebook.net/en_US/fbevents.js');
        fbq('init', '${META_PIXEL_ID}');
        fbq('track', 'PageView');
      `;
      document.head.appendChild(pixelScript);

      // NoScript fallback
      if (!document.querySelector('noscript img[src*="facebook.com/tr"]')) {
        const noscript = document.createElement('noscript');
        const img = document.createElement('img');
        img.height = 1;
        img.width = 1;
        img.style.display = 'none';
        img.src = `https://www.facebook.com/tr?id=${META_PIXEL_ID}&ev=PageView&noscript=1`;
        noscript.appendChild(img);
        document.body.appendChild(noscript);
      }
    };

    // Load Meta Pixel immediately for Facebook ad visitors (fbclid present),
    // otherwise defer 3s to prioritize LCP
    const hasFbclid = window.location.search.includes('fbclid');
    const pixelDelay = hasFbclid ? 0 : 3000;

    if (document.readyState === 'complete') {
      setTimeout(injectMetaPixel, pixelDelay);
    } else {
      window.addEventListener('load', () => {
        setTimeout(injectMetaPixel, pixelDelay);
      });
    }
  }, []);

  // Track page views on route change - DEFERRED to not block rendering
  useEffect(() => {
    const trackPageView = () => {
      // Use requestIdleCallback to defer tracking until browser is idle
      deferUntilIdle(() => {
        // Track with Meta Pixel (if loaded)
        if (typeof window !== 'undefined' && (window as any).fbq) {
          (window as any).fbq('track', 'PageView');
        }

        // Track with OBC tracker using the correct method signature
        if (typeof window !== 'undefined' && (window as any).obcTracker) {
          const tracker = (window as any).obcTracker;
          if (typeof tracker.trackEvent === 'function') {
            tracker.trackEvent('page_view', {
              page_path: location.pathname,
              page_title: document.title,
            });
          }
        }

        // GA4 SPA page_view via centralized module (deduped + Consent-Mode aware).
        trackGooglePageView({
          page_path: location.pathname,
          page_title: document.title,
        });


        // Send via the local tracking proxy using the correct action format
        fetch(LOCAL_FUNCTIONS.visitorTracking, {
          method: 'POST',
          headers: TRACKING_HEADERS,
          body: JSON.stringify({
            action: 'track_event',
            data: {
              session_id: sessionIdRef.current,
              visitor_id: visitorIdRef.current,
              event_type: 'page_view',
              page_url: window.location.href,
              page_path: location.pathname,
              page_title: document.title,
              event_name: 'page_view',
            },
          }),
        }).catch(() => {
          // Silently fail - tracking should not break the app
        });
      }, 1000);
    };

    // Small delay to ensure page title is updated
    const timer = setTimeout(trackPageView, 100);
    return () => clearTimeout(timer);
  }, [location.pathname]);

  return (
    <TrackingContext.Provider
      value={{
        visitorId: visitorIdRef.current,
        sessionId: sessionIdRef.current,
      }}
    >
      <TrackingProviderInner>
        {children}
      </TrackingProviderInner>
    </TrackingContext.Provider>
  );
};

export default TrackingProvider;
